import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import Button from "../../buttons/buttons";
import { Beat } from "../../../plugins/spinners.plugin";
import CLOSE from "../../../assets/images/icons/close.svg";

const COPY = {
  approve: {
    title: "Accept Pay Out Request",
    label: "Approval Reason",
    placeholder: "Why is this request being accepted?",
    submit: "Accept Request",
  },
  reject: {
    title: "Reject Pay Out Request",
    label: "Rejection Reason",
    placeholder: "Why is this request being rejected?",
    submit: "Reject Request",
  },
};

/**
 * Asks for a reason before accepting or rejecting a payout request.
 * @param {"approve" | "reject" | null} action - null keeps the modal closed
 * @param {(reason: string) => void} onSubmit
 */
export default function PayoutDecisionModal({
  action,
  onClose,
  onSubmit,
  submitting,
}) {
  const [reason, setReason] = useState("");
  const copy = COPY[action] || COPY.approve;

  // Start with an empty reason each time the modal opens
  useEffect(() => {
    if (action) setReason("");
  }, [action]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (reason.trim()) onSubmit(reason.trim());
  };

  return (
    <Transition.Root show={!!action} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative w-full transform overflow-hidden rounded-lg bg-white dark:bg-[#121212] px-4 pt-5 pb-4 text-left shadow-xl transition-all sm:my-8 sm:max-w-lg sm:p-6">
                <div className="absolute top-3 right-3">
                  <button
                    type="button"
                    className="bg-[#ffeae5] rounded-md p-2"
                    onClick={onClose}
                  >
                    <span className="sr-only">Close</span>
                    <img src={CLOSE} alt="close" className="w-3 h-3" />
                  </button>
                </div>

                <form onSubmit={handleSubmit}>
                  <Dialog.Title
                    as="h3"
                    className="text-lg font-medium leading-6 text-gray-900 dark:text-white mb-4"
                  >
                    {copy.title}
                  </Dialog.Title>

                  <label
                    htmlFor="payout_reason"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    {copy.label}
                  </label>
                  <textarea
                    id="payout_reason"
                    name="payout_reason"
                    rows={4}
                    value={reason}
                    placeholder={copy.placeholder}
                    onChange={(e) => setReason(e.target.value)}
                    className="block w-full rounded-md border border-gray-300 bg-white dark:bg-transparent dark:text-white p-3 text-sm focus:border-red-600 focus:outline-none focus:ring-2 focus:ring-red-600"
                  />

                  <div className="mt-5 flex gap-4">
                    <Button.Secondary
                      title="Cancel"
                      onClick={onClose}
                      className="w-full"
                    />
                    <Button.Primary
                      title={
                        submitting ? <Beat color={"#ffffff"} /> : copy.submit
                      }
                      type="submit"
                      disabled={!reason.trim() || submitting}
                      className="w-full"
                    />
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
